import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ShieldExclamationIcon,
  ArrowTrendingUpIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { getDashboardStats, getModerationCounts } from '@/services/api';
import MetricCard from '@/components/charts/MetricCard';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import RealtimeActivityFeed from '@/components/modules/dashboard/RealtimeActivityFeed';
import AdminPresenceIndicator from '@/components/layout/AdminPresenceIndicator';
import { formatCurrency, formatNumber } from '@/utils/formatters';

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: getDashboardStats,
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: modCounts } = useQuery({
    queryKey: ['moderationCounts'],
    queryFn: getModerationCounts,
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of platform activity and key metrics
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={formatNumber(stats?.totalUsers)}
          icon={<UsersIcon className="w-5 h-5" />}
          change={stats?.newUsersToday ? (stats.newUsersToday / (stats.totalUsers || 1)) * 100 : 0}
          trend={stats?.newUsersToday ? 'up' : 'neutral'}
          changeLabel={`+${stats?.newUsersToday || 0} today`}
          loading={statsLoading}
        />
        <MetricCard
          title="Total Businesses"
          value={formatNumber(stats?.totalBusinesses)}
          icon={<BuildingStorefrontIcon className="w-5 h-5" />}
          change={stats?.newBusinessesToday ? (stats.newBusinessesToday / (stats.totalBusinesses || 1)) * 100 : 0}
          trend={stats?.newBusinessesToday ? 'up' : 'neutral'}
          changeLabel={`+${stats?.newBusinessesToday || 0} today`}
          loading={statsLoading}
        />
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency((stats?.mrr || 0) * 100)}
          icon={<CurrencyDollarIcon className="w-5 h-5" />}
          trend="up"
          loading={statsLoading}
        />
        <MetricCard
          title="Active Users (24h)"
          value={formatNumber(stats?.activeUsers)}
          icon={<ArrowTrendingUpIcon className="w-5 h-5" />}
          change={stats?.activeUsers && stats?.totalUsers ? (stats.activeUsers / stats.totalUsers) * 100 : 0}
          trend={stats?.activeUsers ? 'up' : 'neutral'}
          changeLabel="of total users"
          loading={statsLoading}
        />
      </div>

      {/* Alerts section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Moderation Queue */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldExclamationIcon className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">Moderation Queue</h2>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <Link
                to="/moderation?tab=conversations"
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="font-medium text-gray-900">Conversation Flags</p>
                    <p className="text-sm text-gray-500">ToS violation reports</p>
                  </div>
                </div>
                <span className="px-3 py-1 text-sm font-medium bg-orange-100 text-orange-700 rounded-full">
                  {modCounts?.pending_conversation_flags || 0}
                </span>
              </Link>

              <Link
                to="/moderation?tab=reviews"
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ShieldExclamationIcon className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-gray-900">Review Flags</p>
                    <p className="text-sm text-gray-500">Reported fake reviews</p>
                  </div>
                </div>
                <span className="px-3 py-1 text-sm font-medium bg-red-100 text-red-700 rounded-full">
                  {modCounts?.pending_review_flags || 0}
                </span>
              </Link>

              <Link
                to="/businesses?filter=pending"
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <BuildingStorefrontIcon className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">Business Profiles</p>
                    <p className="text-sm text-gray-500">Pending approval</p>
                  </div>
                </div>
                <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded-full">
                  {modCounts?.pending_business_moderation || 0}
                </span>
              </Link>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link
                to="/moderation"
                className="text-sm font-medium text-brand hover:text-brand-dark"
              >
                View all moderation items &rarr;
              </Link>
            </div>
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Quick Actions</h2>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/users"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <UsersIcon className="w-8 h-8 text-brand mb-2" />
                <span className="text-sm font-medium text-gray-900">Manage Users</span>
              </Link>
              <Link
                to="/businesses"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <BuildingStorefrontIcon className="w-8 h-8 text-brand mb-2" />
                <span className="text-sm font-medium text-gray-900">Manage Businesses</span>
              </Link>
              <Link
                to="/access-codes"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-2xl mb-2">🔑</span>
                <span className="text-sm font-medium text-gray-900">Access Codes</span>
              </Link>
              <Link
                to="/analytics"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowTrendingUpIcon className="w-8 h-8 text-brand mb-2" />
                <span className="text-sm font-medium text-gray-900">Analytics</span>
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Real-time Activity and Admin Presence */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Activity Feed - takes 2 columns */}
        <div className="lg:col-span-2">
          <RealtimeActivityFeed />
        </div>

        {/* Admin Presence and Platform Health */}
        <div className="space-y-6">
          {/* Online Admins */}
          <AdminPresenceIndicator showDetails />

          {/* Platform Health */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Platform Health</h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm text-gray-700">API Status</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm text-gray-700">Database</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Healthy</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm text-gray-700">Edge Functions</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Running</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm text-gray-700">Realtime</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Connected</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
